import { CONSUMPTION_TAX_RATE } from "@/preferences";
import EstimateItems from "./estimate-items";
import TotalPrice from "./total-price";
import Decimal from "decimal.js";
import { ProjectTaskDefault } from "@/api/project-api";
import EstimateStatus from "@/usecases/orders/estimate-status";

type EstimateId = number;

/**
 * 見積もり
 */
export default class Estimate {
  public constructor(
    public id: EstimateId = 0,
    public estimateNumber: string = "",
    public title: string = "",
    public attention: string = "",
    public mention: string = "",
    public projectId: number = 0,
    public projectName: string = "",
    public projectOwnerName: string = "",
    public projectClientName: string = "",
    public status: string = EstimateStatus.CreatedNew,
    public items: EstimateItems = new EstimateItems(),
    public taxRate: Decimal.Value = CONSUMPTION_TAX_RATE,
    public printsExcludeTax: boolean = false,
    public dateOfIssue: string = "",
    public dateOfContract: string = "",
    public deadline: string = "",
    public profitRate: number = 0,
    public children: Estimate[] = [],
    public taskDefaults: ProjectTaskDefault[] = [],
    public categoryDiscountIds: string[] = []
  ) {}

  public get subtotal(): TotalPrice {
    let result = new TotalPrice(this.items.totalPrice);
    if (this.children.length > 0) {
      let subtotalOfChildren: number = 0;
      this.children.forEach(child => {
        subtotalOfChildren += child.subtotal.toNumber();
      });
      result = new TotalPrice(result.toNumber() + subtotalOfChildren);
    }
    return result;
  }

  public get tax(): Decimal {
    return this.subtotal.tax(this.taxRate);
  }

  public get total() {
    return this.subtotal.withTax(this.taxRate);
  }

  public get loss(): TotalPrice {
    let result = new TotalPrice(this.items.totalLoss);
    if (this.children.length > 0) {
      let lossOfChildren: number = 0;
      this.children.forEach(child => {
        lossOfChildren += child.loss.toNumber();
      });
      result = new TotalPrice(result.toNumber() + lossOfChildren);
    }
    return result;
  }

  public get taxLoss(): number {
    return this.loss.tax(this.taxRate).toNumber();
  }

  public get lossRate(): Decimal {
    return new Decimal(100).minus(this.profitRate);
  }

  public get discount(): number {
    let result: number = this.items.totalDiscount(this.categoryDiscountIds);
    if (this.children.length > 0) {
      let discountOfChildren: number = 0;
      this.children.forEach(child => {
        discountOfChildren += child.discount;
      });
      result += discountOfChildren;
    }
    return result;
  }

  public toJSON(): any {
    return {
      id: this.id,
      estimateNumber: this.estimateNumber,
      title: this.title,
      attention: this.attention,
      mention: this.mention,
      projectId: this.projectId,
      projectName: this.projectName,
      projectClientName: this.projectClientName,
      staffName: this.projectOwnerName,
      status: this.status,
      taxRate: this.taxRate,
      lossRate: this.lossRate.toNumber().toFixed(2),
      subtotal: this.subtotal.toNumber(),
      total: this.total.toNumber(),
      tax: this.tax.toNumber(),
      loss: this.loss.toNumber(),
      taxLoss: this.taxLoss,
      profitRate: Number(this.profitRate).toFixed(2),
      discount: this.discount,
      printsExcludeTax: this.printsExcludeTax,
      items: this.items.toJSON(),
      dateOfIssue: this.dateOfIssue,
      dateOfContract: this.dateOfContract,
      deadline: this.deadline,
      children: this.children.map(child => {
        child.categoryDiscountIds = this.categoryDiscountIds;
        return child.toJSON();
      }),
      taskDefaults: this.taskDefaults,
      categoryDiscountIds: this.categoryDiscountIds
    };
  }
}
